import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../../lib/auth-context.js";
import { usePreferences } from "../../../lib/preferences.js";
import {
  getIndexHistory,
  getNetWorth,
  getNetWorthMetrics,
  type IndexHistoryResponse,
  type NetWorthMetricsResponse,
} from "../../../lib/api-modules/networth.js";
import { listAccounts } from "../../../lib/api-modules/accounts.js";
import type { NetWorthWindow } from "../../../lib/api-modules/types.js";
import { formatCentsCompact } from "../../../lib/util/money.js";
import {
  buildNetWorthBridge,
  describeDebts,
} from "../../../lib/util/net-worth-bridge.js";
import { overlayColor } from "../../../lib/util/chart-theme.js";
import { BalancesAsOf } from "../../accounts/NetWorthBridge.js";
import { Card, Pill, StatCard } from "../../design-system/index.js";
import {
  NetWorthChart,
  type NetWorthChartAccount,
  type NetWorthChartMode,
  type NetWorthChartOverlay,
  type NetWorthChartValueField,
} from "../NetWorthChart.js";
import { ReturnMetrics } from "../ReturnMetrics.js";

/**
 * Phase 13.5 widget — net-worth hero card.
 *
 * Self-contained: owns its own networthQuery + window-selector state.
 * Phase 16d (2026-05-10) lights up:
 *   - Overlay toggle pills (SPY / QQQ / VT / + Custom).
 *   - Per-account breakdown toggle (stacked-area mode).
 *   - Reconstructed-day shading toggle.
 *   - Return-metrics card row beneath the chart.
 */

const DEFAULT_INDEX_TICKERS = ["SPY", "QQQ", "VT"] as const;

// Phase 16e (2026-05-10): chart-basis localStorage key. Persists per-
// browser; falls back to "net_worth" on first visit. The toggle swaps
// only the chart series — hero KPI tile + delta + return metrics stay
// net-worth-anchored. /portfolio Performance tab is the dedicated
// portfolio-focused surface.
const CHART_BASIS_STORAGE_KEY = "hearth:dashboard:chart-basis";
type ChartBasis = "net_worth" | "portfolio";

function readStoredChartBasis(): ChartBasis {
  if (typeof window === "undefined") return "net_worth";
  try {
    const v = window.localStorage.getItem(CHART_BASIS_STORAGE_KEY);
    return v === "portfolio" ? "portfolio" : "net_worth";
  } catch {
    return "net_worth";
  }
}

export function NetWorthHeroWidget() {
  const { user } = useAuth();
  const { privacyMode, theme } = usePreferences();
  const [window, setWindow] = useState<NetWorthWindow>("24m");
  const [overlayPicks, setOverlayPicks] = useState<Set<string>>(new Set());
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [highlightReconstructed, setHighlightReconstructed] = useState(true);
  const [customDraft, setCustomDraft] = useState("");
  const [chartBasis, setChartBasis] = useState<ChartBasis>(readStoredChartBasis);
  const valueField: NetWorthChartValueField =
    chartBasis === "portfolio" ? "portfolio_value_cents" : "net_worth_cents";

  const persistChartBasis = (next: ChartBasis) => {
    setChartBasis(next);
    if (typeof globalThis !== "undefined" && globalThis.localStorage) {
      try {
        globalThis.localStorage.setItem(CHART_BASIS_STORAGE_KEY, next);
      } catch {
        // Storage quota exceeded or disabled; UI keeps the in-memory value.
      }
    }
  };

  const networthQuery = useQuery({
    queryKey: ["networth", window],
    queryFn: () => getNetWorth(window),
    enabled: !!user,
  });
  const metricsQuery = useQuery<NetWorthMetricsResponse>({
    queryKey: ["networth-metrics", window],
    queryFn: () => getNetWorthMetrics(window),
    enabled: !!user,
  });

  const snapshots = useMemo(
    () => networthQuery.data?.snapshots ?? [],
    [networthQuery.data?.snapshots],
  );
  const latest = networthQuery.data?.latest ?? null;
  const previous =
    snapshots.length >= 2 ? snapshots[snapshots.length - 2] : null;
  const deltaCents =
    latest && previous ? latest.net_worth_cents - previous.net_worth_cents : null;

  // Overlay queries — one per selected ticker. TanStack Query caches per
  // (ticker, window) so re-clicking a pill is instant. 24h staleTime
  // since ETF closes don't change post-close.
  const startDate = snapshots[0]?.date;
  const endDate = snapshots[snapshots.length - 1]?.date;
  const tickers = useMemo(
    () => Array.from(overlayPicks),
    [overlayPicks],
  );
  // Stack one useQuery per ticker: keep the array hook stable by
  // building a comma-joined queryKey + invoking via Promise.all from
  // within a single useQuery. Keeps the effective hook count fixed.
  const overlayQuery = useQuery<IndexHistoryResponse[]>({
    queryKey: ["index-history", tickers.join(","), startDate, endDate],
    queryFn: async () => {
      if (!startDate || !endDate || tickers.length === 0) return [];
      return Promise.all(
        tickers.map((t) => getIndexHistory(t, startDate, endDate)),
      );
    },
    enabled: tickers.length > 0 && !!startDate && !!endDate,
    staleTime: 24 * 60 * 60 * 1000, // 24h
  });

  // Account name resolution for the per-account breakdown.
  const accountsQuery = useQuery({
    queryKey: ["accounts-summary"],
    queryFn: () => listAccounts(),
    enabled: !!user,
    staleTime: 60_000,
  });

  const overlays = useMemo<NetWorthChartOverlay[]>(() => {
    if (showBreakdown) return [];
    if (!overlayQuery.data) return [];
    return overlayQuery.data.map((resp, i) => ({
      key: resp.ticker,
      label: resp.ticker,
      color: overlayColor(i, theme === "dark" ? "dark" : "light"),
      bars: resp.bars.map((b) => ({
        date: b.date,
        // Tiingo prices are dollar floats; convert to integer cents for
        // chart-side normalization math.
        close_cents: Math.round(b.close * 100),
      })),
    }));
  }, [overlayQuery.data, theme, showBreakdown]);

  const accountBreakdown = useMemo<NetWorthChartAccount[]>(() => {
    if (!showBreakdown) return [];
    const accounts = accountsQuery.data?.accounts ?? [];
    // Filter to accounts that actually appear in any snapshot's
    // breakdown for the selected basis — avoids rendering zero-bars for
    // accounts with no historical contribution. Phase 16e (2026-05-10):
    // when chart basis is "portfolio", read `portfolio_by_account`
    // (cash + liability accounts have no entry); when "net_worth",
    // read `account_breakdown` (existing behavior). Sort by current
    // value desc so the largest stacks at the bottom.
    const seenIds = new Set<string>();
    for (const s of snapshots) {
      const breakdown =
        valueField === "portfolio_value_cents"
          ? s.portfolio_by_account
          : s.account_breakdown;
      for (const id of Object.keys(breakdown ?? {})) {
        seenIds.add(id);
      }
    }
    return accounts
      .filter((a) => seenIds.has(a.account_id) && !a.is_archived)
      .sort((a, b) => b.total_value_cents - a.total_value_cents)
      .map((a, i) => ({
        account_id: a.account_id,
        name: a.name,
        color: overlayColor(i, theme === "dark" ? "dark" : "light"),
      }));
  }, [snapshots, accountsQuery.data, showBreakdown, theme, valueField]);

  const chartMode: NetWorthChartMode = showBreakdown ? "stacked-area" : "line";

  const money = (cents: number) =>
    formatCentsCompact(cents, { hidden: privacyMode });
  const bridge = latest
    ? buildNetWorthBridge({
        investments_cents: latest.portfolio_value_cents ?? 0,
        cash_cents: latest.cash_cents,
        margin_cents: latest.margin_cents,
        net_worth_cents: latest.net_worth_cents,
      })
    : null;

  const togglePick = (ticker: string) => {
    setOverlayPicks((prev) => {
      const next = new Set(prev);
      if (next.has(ticker)) next.delete(ticker);
      else next.add(ticker);
      return next;
    });
  };

  const submitCustom = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const t = customDraft.trim().toUpperCase();
    if (!t) return;
    setOverlayPicks((prev) => new Set([...prev, t]));
    setCustomDraft("");
  };

  return (
    <>
      <Card className="mb-6" elevated>
        <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-muted)]">
          Net worth
        </p>
        {networthQuery.isLoading ? (
          <p
            className="mt-2 text-[var(--color-muted)]"
            data-testid="networth-loading"
          >
            Loading…
          </p>
        ) : networthQuery.isError ? (
          <p
            className="mt-2 text-[var(--color-danger)]"
            data-testid="networth-error"
          >
            Couldn't load net worth.{" "}
            {networthQuery.error instanceof Error
              ? networthQuery.error.message
              : ""}
          </p>
        ) : latest ? (
          <div className="mt-1 flex flex-wrap items-baseline gap-3">
            <p
              className="font-display text-5xl font-medium tracking-tight tabular-nums text-[var(--color-text)]"
              data-testid="networth-headline"
            >
              {formatCentsCompact(latest.net_worth_cents, {
                hidden: privacyMode,
              })}
            </p>
            {deltaCents !== null && (
              <Pill
                tone={deltaCents >= 0 ? "success" : "danger"}
                className="font-mono"
              >
                <span data-testid="networth-delta">
                  {deltaCents >= 0 ? "+" : ""}
                  {formatCentsCompact(deltaCents, { hidden: privacyMode })}{" "}
                  since previous snapshot
                </span>
              </Pill>
            )}
          </div>
        ) : (
          <p
            className="mt-2 text-[var(--color-muted)]"
            data-testid="networth-empty"
          >
            No snapshots yet — the first one runs nightly at 7pm ET.
          </p>
        )}

        <div className="mt-5 flex flex-wrap items-center gap-1.5">
          {(["24m", "5y", "all"] as const).map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => setWindow(w)}
              className={
                window === w
                  ? "rounded-md bg-[var(--color-accent-soft)] px-3 py-1 text-xs font-medium text-[var(--color-accent)]"
                  : "rounded-md px-3 py-1 text-xs text-[var(--color-muted)] hover:bg-[var(--color-elevated)] hover:text-[var(--color-text)]"
              }
            >
              {w === "24m" ? "24M" : w === "5y" ? "5Y" : "All"}
            </button>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {DEFAULT_INDEX_TICKERS.map((t, i) => {
            const active = overlayPicks.has(t);
            const color = overlayColor(i, theme === "dark" ? "dark" : "light");
            return (
              <button
                key={t}
                type="button"
                onClick={() => togglePick(t)}
                disabled={showBreakdown}
                style={
                  active && !showBreakdown
                    ? { borderColor: color, color }
                    : undefined
                }
                className={
                  active && !showBreakdown
                    ? "rounded-full border px-3 py-1 text-xs font-medium"
                    : showBreakdown
                      ? "rounded-full border border-[var(--color-border-subtle)] px-3 py-1 text-xs text-[var(--color-muted)] opacity-50"
                      : "rounded-full border border-[var(--color-border-default)] px-3 py-1 text-xs text-[var(--color-muted)] hover:text-[var(--color-text)]"
                }
              >
                {active ? "− " : "+ "}
                {t}
              </button>
            );
          })}
          {/* Custom-ticker entry. Uppercases on submit; pill renders in
              palette slot 4+. */}
          {!showBreakdown && (
            <form onSubmit={submitCustom} className="inline-flex items-center">
              <input
                type="text"
                value={customDraft}
                onChange={(e) => setCustomDraft(e.target.value)}
                placeholder="+ Custom"
                className="w-24 rounded-full border border-dashed border-[var(--color-border-default)] bg-transparent px-3 py-1 text-xs uppercase text-[var(--color-text)] placeholder:normal-case placeholder:text-[var(--color-muted)] focus:border-[var(--color-accent)] focus:outline-none"
                aria-label="Add custom ticker overlay"
              />
            </form>
          )}
          {/* Overlay-picks beyond the default trio render as removable
              chips so the user can clear customs. */}
          {Array.from(overlayPicks)
            .filter(
              (t) => !(DEFAULT_INDEX_TICKERS as readonly string[]).includes(t),
            )
            .map((t, idx) => {
              const color = overlayColor(
                DEFAULT_INDEX_TICKERS.length + idx,
                theme === "dark" ? "dark" : "light",
              );
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => togglePick(t)}
                  disabled={showBreakdown}
                  style={
                    !showBreakdown ? { borderColor: color, color } : undefined
                  }
                  className="rounded-full border px-3 py-1 text-xs font-medium disabled:opacity-50"
                >
                  − {t}
                </button>
              );
            })}
          <span className="mx-2 h-4 w-px bg-[var(--color-border-default)]" />
          <button
            type="button"
            onClick={() => setShowBreakdown((b) => !b)}
            className={
              showBreakdown
                ? "rounded-full bg-[var(--color-accent-soft)] px-3 py-1 text-xs font-medium text-[var(--color-accent)]"
                : "rounded-full border border-[var(--color-border-default)] px-3 py-1 text-xs text-[var(--color-muted)] hover:text-[var(--color-text)]"
            }
          >
            {showBreakdown ? "− Per-account breakdown" : "+ Per-account breakdown"}
          </button>
          <button
            type="button"
            onClick={() => setHighlightReconstructed((s) => !s)}
            className={
              highlightReconstructed
                ? "rounded-full bg-[var(--color-elevated)] px-3 py-1 text-xs font-medium text-[var(--color-text)]"
                : "rounded-full border border-[var(--color-border-default)] px-3 py-1 text-xs text-[var(--color-muted)] hover:text-[var(--color-text)]"
            }
            title="Shade the chart region built from reconstructed (backfilled) snapshots"
          >
            {highlightReconstructed ? "✓ " : ""}Highlight reconstructed
          </button>
        </div>

        {/* Phase 16e (2026-05-10): chart-basis toggle. Net worth =
            assets − liabilities (default). Portfolio = positions only,
            no cash, no margin, no liabilities — the honest "did the
            equity portion beat SPY?" basis. Stacked-area mode swaps
            the per-account breakdown to match. */}
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-[var(--color-muted)]">Chart basis:</span>
          {(["net_worth", "portfolio"] as const).map((b) => (
            <button
              key={b}
              type="button"
              onClick={() => persistChartBasis(b)}
              className={
                chartBasis === b
                  ? "rounded-full bg-[var(--color-accent-soft)] px-3 py-1 text-xs font-medium text-[var(--color-accent)]"
                  : "rounded-full border border-[var(--color-border-default)] px-3 py-1 text-xs text-[var(--color-muted)] hover:text-[var(--color-text)]"
              }
              data-testid={`chart-basis-${b}`}
              title={
                b === "portfolio"
                  ? "Positions only. Excludes cash sleeves, margin, and liabilities — honest comparison vs SPY."
                  : "Assets minus liabilities. Spending drains cash, which can drag the line below SPY even if your equity portion is fine."
              }
            >
              {b === "portfolio" ? "Portfolio (positions)" : "Net worth"}
            </button>
          ))}
        </div>

        <div className="mt-4">
          <NetWorthChart
            snapshots={snapshots}
            hideValues={privacyMode}
            mode={chartMode}
            overlays={overlays}
            accountBreakdown={accountBreakdown}
            highlightReconstructed={highlightReconstructed}
            valueField={valueField}
          />
        </div>

        <div className="mt-4">
          <ReturnMetrics
            metrics={metricsQuery.data}
            isLoading={metricsQuery.isLoading}
            hideValues={privacyMode}
          />
        </div>
      </Card>

      {latest && bridge && (
        <div className="mb-6 grid gap-4 md:grid-cols-3">
          <StatCard
            label="Total assets"
            value={formatCentsCompact(latest.total_assets_cents, {
              hidden: privacyMode,
            })}
            sub={`Investments ${money(bridge.investments_cents)} + cash ${money(bridge.cash_cents)}`}
          />
          <StatCard
            label="Total liabilities"
            value={formatCentsCompact(latest.total_liabilities_cents, {
              hidden: privacyMode,
            })}
            sub={describeDebts(bridge, money)}
          />
          <StatCard
            label="Net worth"
            value={formatCentsCompact(latest.net_worth_cents, {
              hidden: privacyMode,
            })}
            sub="Assets − liabilities"
          />
          <div className="md:col-span-3">
            <BalancesAsOf
              date={accountsQuery.data?.totals.balances_as_of_date ?? null}
            />
          </div>
        </div>
      )}
    </>
  );
}
